import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDICATORS = [
  { code: 'NY.GDP.MKTP.KD.ZG', name: 'GDP Growth (%)', unit: '%' },
  { code: 'FP.CPI.TOTL.ZG', name: 'Inflation Rate (%)', unit: '%' },
  { code: 'SL.UEM.TOTL.ZS', name: 'Unemployment (%)', unit: '%' },
  { code: 'NE.TRD.GNFS.ZS', name: 'Trade / GDP (%)', unit: '%' },
  { code: 'GC.DOD.TOTL.GD.ZS', name: 'Govt Debt / GDP (%)', unit: '%' },
  { code: 'DT.DOD.DECT.GN.ZS', name: 'External Debt / GNI (%)', unit: '%' },
];

const COLORS = ['#004ac6', '#00A651', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

function TrendChart() {
  const [indicator, setIndicator] = useState(INDICATORS[0].code);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [showAll, setShowAll] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/wb-trends/${indicator}`, { timeout: 50000 })
      .then(res => {
        setData(res.data);
        if (res.data?.trends) {
          setSelectedCountries(Object.keys(res.data.trends));
          setShowAll(true);
        }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [indicator]);

  const allCountries = data?.trends ? Object.keys(data.trends) : [];

  const toggleCountry = useCallback((country) => {
    setSelectedCountries(prev => {
      if (prev.includes(country)) {
        const next = prev.filter(c => c !== country);
        setShowAll(false);
        return next;
      }
      const next = [...prev, country];
      if (next.length === allCountries.length) setShowAll(true);
      return next;
    });
  }, [allCountries.length]);

  const toggleAll = () => {
    if (showAll) {
      setSelectedCountries([]);
      setShowAll(false);
    } else {
      setSelectedCountries([...allCountries]);
      setShowAll(true);
    }
  };

  const displayCountries = selectedCountries.filter(c => allCountries.includes(c));

  const chartData = data?.years?.map(year => {
    const point = { year };
    displayCountries.forEach(country => {
      point[country] = data.trends[country]?.[year] ?? null;
    });
    return point;
  }) || [];

  const currentIndicator = INDICATORS.find(i => i.code === indicator);

  return (
    <div className="bg-white rounded-xl shadow-sm p-5" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#004ac6]" />
          <h2 className="text-sm font-bold text-[#0b1c30] tracking-tight">Country Comparison</h2>
        </div>
        <Select value={indicator} onValueChange={setIndicator}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="trend-indicator-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INDICATORS.map(ind => (
              <SelectItem key={ind.code} value={ind.code}>{ind.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Country Picker */}
      {allCountries.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="country-picker">
          <button onClick={toggleAll} className={`px-2 py-1 rounded text-[10px] font-semibold transition-all ${showAll ? 'bg-[#004ac6] text-white' : 'bg-[#f8f9ff] text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`} data-testid="select-all-countries">
            All
          </button>
          {allCountries.map((country, i) => {
            const active = displayCountries.includes(country);
            const color = COLORS[i % COLORS.length];
            return (
              <button key={country} onClick={() => toggleCountry(country)} data-testid={`country-toggle-${country.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-2 py-1 rounded text-[10px] font-semibold transition-all flex items-center gap-1 ${active ? 'text-white' : 'bg-[#f8f9ff] text-[#434655] border border-[#c3c6d7]/40 hover:bg-[#eff4ff]'}`}
                style={active ? { backgroundColor: color } : undefined}>
                {country}
                {active && <X className="w-2.5 h-2.5" />}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-5 h-5 animate-spin text-[#004ac6] mr-2" />
          <span className="text-xs text-[#434655]">Loading World Bank data...</span>
        </div>
      ) : chartData.length > 0 && displayCountries.length > 0 ? (
        <>
          <div className="text-[10px] text-[#434655] mb-2">
            {currentIndicator?.name} — {displayCountries.length} of {allCountries.length} countries ({data?.years?.[0]}–{data?.years?.[data.years.length - 1]})
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="year" tick={{ fontSize: 10, fill: '#434655' }} />
              <YAxis tick={{ fontSize: 10, fill: '#434655' }} unit={currentIndicator?.unit === '%' ? '%' : ''} width={45} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E5E7EB', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                formatter={(value, name) => [value != null ? `${value.toFixed(1)}${currentIndicator?.unit || ''}` : 'N/A', name]}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {displayCountries.map((country, i) => {
                const origIdx = allCountries.indexOf(country);
                return (
                  <Line key={country} type="monotone" dataKey={country}
                    stroke={COLORS[origIdx % COLORS.length]} strokeWidth={2}
                    dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
          <div className="text-[9px] text-[#9CA3AF] mt-2">Source: World Bank Open Data</div>
        </>
      ) : displayCountries.length === 0 ? (
        <div className="text-center py-12 text-[#434655]">
          <p className="text-xs italic">Select at least one country to compare.</p>
        </div>
      ) : (
        <div className="text-center py-12 text-[#434655]">
          <p className="text-xs italic">No trend data available. Data is fetched from World Bank on first load.</p>
        </div>
      )}
    </div>
  );
}

export default TrendChart;
